import { signedMoney } from './format.js';

export const EMPTY_CREDIT_ROWS = [];

export const rowPrice = (row = {}) => Number(
  row.dealerCost
  ?? row.budget
  ?? row.mrr
  ?? row.dealerPrice
  ?? row.price
  ?? 0
);

export const productCode = (row = {}) => (
  row.productCode
  || row.normalizedProductCode
  || row.oemProductCode
  || row['OEM Product Code']
  || ''
);

export const creditSourceEvidence = (product = {}) => {
  const row = product.row || {};
  return {
    source: product.source || 'custom',
    productCode: product.productCode || product.code || productCode(row),
    price: Number.isFinite(Number(product.price)) ? Number(product.price) : null,
    orderItemId: product.orderItemId || row.orderItemId || row.id || row.Id || row['OrderItem ID'] || '',
    referenceId: product.referenceId || row.referenceId || row.referenceID || row['DPE Reference ID'] || '',
    salesforceAccountId: product.salesforceAccountId || row.salesforceAccountId || row.accountId || row.Account_ID__c || row['Account ID'] || '',
    salesforceAccountName: product.salesforceAccountName || row.salesforceAccountName || row.accountName || row['Account Name'] || '',
    rowNumber: row.rowNumber || row.row_number || '',
    rowKey: row.rowKey || row.sourceRowKey || row.id || '',
    effectiveDate: product.effectiveDate || row.effectiveDate || row['Effective Date'] || '',
  };
};

export const candidateLabel = (row = {}) => [
  row.bac ? `BAC ${row.bac}` : '',
  row.dealerName || row.salesforceAccountName || row.name || row.accountMatchSummary?.salesforceAccountName || row.accountMatch?.salesforceAccountName || '',
].filter(Boolean).join(' - ');

export const normalizeAccount = (row = {}) => ({
  bac: row.bac,
  dealerName: row.dealerName || row.name || '',
  salesforceAccountId: row.salesforceAccountId || row.salesforceId || row.id || '',
  salesforceAccountName: row.salesforceAccountName || row.name || '',
  label: row.label || candidateLabel(row),
});

export const accountOptionsFromRows = (rows = []) => rows
  .filter((row) => row.bac)
  .map(normalizeAccount)
  .sort((left, right) => left.label.localeCompare(right.label));

export const accountChoicesForBac = ({ rows = [], selectedAccount = null } = {}) => {
  const scopedRows = [...rows, ...(selectedAccount ? [selectedAccount] : [])]
    .filter((row) => row.bac === selectedAccount?.bac)
    .map((row) => (row.label ? row : normalizeAccount(row)));
  const byAccount = new Map();
  scopedRows.forEach((row) => {
    const key = row.salesforceAccountId || row.salesforceAccountName || row.label;
    if (key && !byAccount.has(key)) byAccount.set(key, row);
  });
  return [...byAccount.values()].sort((left, right) => left.label.localeCompare(right.label));
};

export const catalogOptionsFromRows = (rows = []) => rows
  .filter((row) => row.productCode)
  .map((row) => ({
    code: row.productCode,
    name: row.displayName || row.productCode,
    price: Number(row.standardPrice || 0),
    row,
    label: `${row.productCode} - ${row.displayName || 'Catalog product'}${row.standardPrice ? ` (${signedMoney(row.standardPrice)})` : ''}`,
  }));

export const productSelectionFields = (product = {}) => {
  const code = product.productCode || product.code || productCode(product.row || {});
  const price = Number.isFinite(Number(product.price)) ? Number(product.price) : rowPrice(product.row || {});
  return { code, price };
};

export const parseCreditAmount = (value) => {
  if (value === null || value === undefined) return NaN;
  const normalized = String(value).trim().replace(/,/g, '');
  if (!normalized) return NaN;
  return Number(normalized);
};

export const buildCreditPayload = ({
  runId,
  selectedAccount,
  selectedProduct,
  customProductCode,
  customPrice,
  fullCreditAmount,
  notes,
} = {}) => {
  const code = String(customProductCode || '').trim();
  return {
    runId,
    bac: selectedAccount.bac,
    dealerName: selectedAccount.dealerName,
    salesforceAccountId: selectedAccount.salesforceAccountId,
    salesforceAccountName: selectedAccount.salesforceAccountName,
    productCode: code,
    productName: selectedProduct?.productName || selectedProduct?.name || code,
    fullCreditAmount: parseCreditAmount(fullCreditAmount),
    notes,
    sourceEvidence: selectedProduct
      ? creditSourceEvidence(selectedProduct)
      : { source: 'custom', productCode: code, price: Number(customPrice) || null },
  };
};
